import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useInView } from 'react-intersection-observer'
import { TeamsContext } from '../../../../app/contexts/TeamsContext'
import { useAuth } from '@/app/hooks/useAuth'
import { DateRange } from 'react-day-picker'
import {
  endOfDay,
  endOfMonth,
  format,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isThisMonth,
  isToday,
  isYesterday,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import { GoalsValidation } from '@/view/components/GoalsForm'
import { teamsService } from '@/app/services/teamsService'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

const schema = z.object({
  name: z.string().min(1, 'O nome é obrigatório'),
  code: z
    .string()
    .min(1, 'A Url é obrigatória')
    .refine(
      (f) => !f.match(/[^a-zA-Z\d]/g),
      'A Url não pode conter espaço ou qualquer caracter especial',
    )
    .refine(async (f) => {
      if (!f || !(f.length > 0)) return false
      return await teamsService.checkCodeIsAvailable(f)
    }, 'Esse codigo não está disponível'),
  about: z.string(),
  // .min(1, 'A descrição é obrigatória'),
  supportPhone: z
    .string()
    .optional()
    .transform((f) => (f ? '55' + f.replace(/\D/g, '') : undefined))
    .refine(
      (f) => (f ? f.length > 11 && f.length < 14 : !f),
      'Telefone inválido',
    ),
  goal: GoalsValidation,
})

export type FormCreateTeamData = z.infer<typeof schema>

export const useTeamsPageController = () => {
  const { id: supervisorId } = useParams()
  const { ref, inView } = useInView()
  const navigate = useNavigate()
  const methods = useForm<FormCreateTeamData>({
    resolver: zodResolver(schema),
  })

  const location = useLocation()
  const supervisorName = location.state?.supervisorName || 'Sem supervisor'
  const isFromManagerDetails = Boolean(location.state)

  const {
    teams: teamsQuery,
    setSearchValue,
    searchValue,
    setFilterParams,
    params,
  } = useContext(TeamsContext)

  const [open, setOpen] = useState(false)
  const [sort, setSort] = useState<string>('transactions_total')
  const [order, setOrder] = useState<string>('-1')
  const initMonth = startOfMonth(new Date()).toISOString()
  const endMonth = endOfMonth(new Date()).toISOString()
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>({
    from: new Date(params?.startDate ? params?.startDate : initMonth),
    to: new Date(params?.endDate ? params?.endDate : endMonth),
  })
  const { isAdmin, PERMISSIONS } = useAuth()

  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = teamsQuery

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleSortChange = (value: string) => {
    setSort(value)
    setFilterParams({ sort: value, order })
  }

  const handleOrderChange = () => {
    const newOrder = order === '-1' ? '1' : '-1'
    setOrder(newOrder)
    setFilterParams({ order: newOrder })
  }

  const onSelect = async (d?: DateRange) => {
    if (!d) {
      setSelectedDate(undefined)
      setFilterParams({
        startDate: undefined,
        endDate: undefined,
      })
      return
    }

    setSelectedDate({
      from: d?.from ? startOfDay(d.from) : d?.from,
      to: d?.to ? endOfDay(d.to) : d?.to,
    })

    if (d?.from && d?.to) {
      setFilterParams({
        startDate: format(startOfDay(d.from), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        endDate: format(endOfDay(d.to), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
      })

      await new Promise((resolve) => setTimeout(resolve, 200))
      setOpen(false)
    }
  }

  const getLabel = () => {
    if (selectedDate && selectedDate.from && selectedDate.to) {
      const { from, to } = selectedDate

      if (isSameDay(from, to)) {
        if (isYesterday(from)) return 'Ontem'
        if (isToday(from)) return 'Hoje'

        return format(from, 'dd/MM/yyyy')
      }

      if (isFirstDayOfMonth(from) && isLastDayOfMonth(to) && isThisMonth(from))
        return 'Este mês'

      return `De ${format(from, 'dd/MM')} à ${format(to, 'dd/MM')}`
    }

    return 'Selecione uma data'
  }

  const isFetchingRef = useRef(false)

  const teams = useMemo(() => {
    if (!data) return []
    const seen = new Set<string>()
    return data.pages
      .flatMap((page) =>
        supervisorId
          ? page.teams.filter((t) => t.name !== 'Sem Equipe')
          : page.teams,
      )
      .filter((team) => {
        if (seen.has(team._id)) return false
        seen.add(team._id)
        return true
      })
  }, [data, supervisorId])

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isFetchingRef.current
    ) {
      isFetchingRef.current = true
      fetchNextPage().finally(() => {
        isFetchingRef.current = false
      })
    }
  }, [fetchNextPage, inView, hasNextPage, isFetchingNextPage])

  const handleGoToTeamsPage = (teamsId: string) => {
    navigate(`/times/detalhes/${teamsId}`)
  }

  return {
    handleGoToTeamsPage,
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ref,
    methods,
    searchValue,
    handleSearchInputChange,
    setFilterParams,
    params,
    open,
    setOpen,
    getLabel,
    onSelect,
    selectedDate,
    sort,
    order,
    handleSortChange,
    handleOrderChange,
    teams,
    isAdmin,
    supervisorName,
    isFromManagerDetails,
    supervisorId,
    PERMISSIONS,
  }
}
